"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.useFinaliseOrderFallback = void 0;
var _auth = require("@ori/auth");
var _checkoutUtils = require("@ori/checkout-utils");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _shoppingContext = require("@ori/shopping-context");
var _react = require("react");
var _constants = require("../constants");
var _logger = require("../logger");
var _types = require("../types");
const useFinaliseOrderFallback = (param)=>{
    let { isFallbackRunning, setIsFallbackRunning, setOrderState, isOnlinePaymentMethod, isOrderReviewSkipped } = param;
    const sessionId = (0, _shoppingContext.useSessionId)();
    const { shoppingContext: { customerId = _auth.NOT_LOGGED_IN_USER_ID } = {} } = (0, _shoppingContext.useShoppingContext)();
    const shouldRequestSession = (0, _react.useRef)(true);
    const timer = (0, _react.useRef)(_constants.PLACE_ORDER_FALLBACK_TIMEOUT);
    const timerInterval = (0, _react.useRef)();
    const apiClient = (0, _globalApiClientReact.useApiClient)();
    const timerFallback = (0, _react.useCallback)(()=>{
        // (5a) Interval is cleared to avoid unnecessary requests
        clearInterval(timerInterval.current);
        // (6a) Timer is reset to original value for possible future fallback usage
        timer.current = _constants.PLACE_ORDER_FALLBACK_TIMEOUT;
        // (7a) Generic error is displayed, order state is set to failed to enable finalize order button and fallback is stopped
        (0, _checkoutUtils.showErrorNotification)('genericError');
        setOrderState(_types.OrderState.Failed);
        setIsFallbackRunning(false);
    }, [
        setIsFallbackRunning,
        setOrderState
    ]);
    const getSession = (0, _react.useCallback)(async ()=>{
        try {
            (0, _logger.logDebug)('getShoppingSession requested with remaining timer:', timer.current);
            await (0, _globalApiClientReact.getShoppingSession)(apiClient, sessionId);
        // (3a) If shopping session exists, interval will continue with reduced timer
        } catch (error) {
            const apiError = error;
            (0, _logger.logDebug)('error catcher');
            // (3b) If shopping session doesn't exist, it means order has been placer already and we can continue as if original place order request would pass
            if (apiError.status === 404) {
                // (4b) Interval is cleared, because it's no longer needed
                clearInterval(timerInterval.current);
                // (5b) Order data are fetched based on original sessionId
                const { data: { collection: [order] } } = await (0, _globalApiClientReact.getOrders)(apiClient, {
                    customerId,
                    top: 1,
                    searchPhrase: sessionId
                });
                // Any new requests are avoided by shouldRequestSession ref since session doesn't exist already
                shouldRequestSession.current = false;
                // (6b) Redirect is applied based on order data and configuration
                const baseUrl = isOrderReviewSkipped && isOnlinePaymentMethod && typeof (order === null || order === void 0 ? void 0 : order.onlinePaymentId) === 'number' ? "/finance/payment/ProcessPayment?onlinePaymentId=".concat(order.onlinePaymentId, "&clientApp=1") : "/shopping/order-confirm?orderId=".concat(order === null || order === void 0 ? void 0 : order.orderId, "&onlinePaymentId=").concat(typeof (order === null || order === void 0 ? void 0 : order.onlinePaymentId) === 'number' ? order.onlinePaymentId : 0);
                window.location.replace(baseUrl);
            }
        }
    }, [
        apiClient,
        customerId,
        isOnlinePaymentMethod,
        isOrderReviewSkipped,
        sessionId
    ]);
    const timerFunction = (0, _react.useCallback)(()=>{
        if (timer.current > 0) {
            // (2) Check if session exists and reduce timer by 1000ms
            void getSession();
            timer.current -= 1000;
            (0, _logger.logDebug)('timer.current:', timer.current);
        }
    }, [
        getSession
    ]);
    (0, _react.useEffect)(()=>{
        if (shouldRequestSession.current && timer.current > 0 && isFallbackRunning) {
            // (1) Start of the interval for timerFunction after gateway timed out during place order request
            timerInterval.current = setInterval(timerFunction, 1000);
            setTimeout(()=>{
                // (4a) Session was active during whole interval, so fallback will be applied
                timerFallback();
            }, _constants.PLACE_ORDER_FALLBACK_TIMEOUT);
        }
    }, [
        getSession,
        isFallbackRunning,
        timerFallback,
        timerFunction
    ]);
    return {
        timer
    };
};
exports.useFinaliseOrderFallback = useFinaliseOrderFallback;
